/* Product-card tilt.
 *
 * The 21st.dev versions of this are React + Framer Motion. None of that is
 * needed: the effect is a perspective rotation driven by pointer position,
 * Written natively it is one delegated listener for the whole page, and it
 * only ever writes two CSS custom properties, both feeding `transform` --
 * composited, so no layout and no repaint of the card's contents.
 *
 * Deliberately inert when: the user asks for reduced motion, the pointer is
 * coarse (a finger has no hover, and a tilt that fires on tap reads as a bug),
 * or the grid opted out.
 */
(function () {
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  var calm = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!fine.matches || calm.matches) return;

  var MAX = 7;            // degrees of rotation at the card's edge. TUNE HERE.
  var active = null;
  var frame = 0;
  var pending = null;

  function apply() {
    frame = 0;
    if (!active || !pending) return;
    var el = active, r = pending;
    var px = (r.x - r.rect.left) / r.rect.width;
    var py = (r.y - r.rect.top) / r.rect.height;
    el.style.setProperty('--tu-ry', ((px - 0.5) * 2 * MAX).toFixed(2) + 'deg');
    el.style.setProperty('--tu-rx', ((0.5 - py) * 2 * MAX).toFixed(2) + 'deg');
  }

  function clear(el) {
    if (!el) return;
    el.classList.remove('tu-p--tilted');
    el.style.removeProperty('--tu-rx');
    el.style.removeProperty('--tu-ry');
  }

  document.addEventListener('pointermove', function (e) {
    var card = e.target.closest ? e.target.closest('.tu-p') : null;
    if (card && !card.closest('[data-tu-tilt="off"]')) {
      if (card !== active) { clear(active); active = card; card.classList.add('tu-p--tilted'); }
      pending = { x: e.clientX, y: e.clientY, rect: card.getBoundingClientRect() };
      if (!frame) frame = requestAnimationFrame(apply);
    } else if (active) {
      clear(active); active = null; pending = null;
    }
  }, { passive: true });

  document.addEventListener('pointerleave', function () {
    clear(active); active = null; pending = null;
  }, { passive: true });
})();

/* Skeleton loading (perf plan A18, 2026-09-14) -- separate IIFE because the
 * tilt effect above returns early on reduced motion / a coarse pointer, and
 * the shimmer-gating classes below have to run regardless of that. Loaded on
 * every template (A11), so this is what makes collection/search/product
 * grids shimmer, not just the homepage bands tu-loader.js drives.
 */
(function () {
  // Audit fix 2026-09-14 (client: "the shimmer does not work on any image"):
  // the first cut gated the shimmer on the band tu-loader.js was CURRENTLY
  // filling, which on a fast connection is a window of tens of milliseconds,
  // and a box queued behind it -- the one a visitor is actually looking at --
  // never shimmered at all. The gate is now "waiting AND near the viewport":
  // every deferred (data-tu-src) or lazy image is observed itself, gets
  // is-near within half a screen of the viewport, and the CSS shimmers an
  // is-near image until is-loaded. Still bounded by the viewport, not the page.
  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) { entries.forEach(function (e) { e.target.classList.toggle('is-near', e.isIntersecting); }); }, { rootMargin: '50% 0px' }) : null;
  function mark(img) { img.classList.add('is-loaded'); }
  // A deferred image starts with a 1x1 data: GIF as src; that GIF's own load
  // event must not count (audit fix 2026-09-14 -- it marked cards loaded
  // before the real image was even requested, so the shimmer never showed).
  // tu-loader.js removes data-tu-src when it swaps the real src in.
  function onLoad(img) { if (img.hasAttribute('data-tu-src')) return; mark(img); img.removeEventListener('load', img.__tuOnLoad); img.removeEventListener('error', img.__tuOnLoad); }
  // 2026-10-06, skeleton sweep: was a one-shot scan at page load, so images inserted later (the cart drawer
  // replaces its body by innerHTML) were never observed. observe(root) scans a subtree and skips images it has
  // already seen; any script that injects markup can call window.TuSkel.observe(thatElement).
  function observe(root) {
    // 2026-10-06 (template sweep P1/C1): fetchpriority=high photos (the PDP main photo, the first two cards of every grid)
    // are under the same behaviour now: shimmer, hidden until complete, fade in. Not the hero tiles (their own preview
    // system), the collection header photo (dark banner behind it), the header logos or the cart drawer.
    (root || document).querySelectorAll('img[loading="lazy"], img[data-tu-src], img[fetchpriority="high"]').forEach(function (img) {
      if (img.__tuSkel) return;
      if (img.classList.contains('tu-marq__logo')) return;   // the brand strip has its own bar + fade (tu-brand-strip.liquid)
      if (img.getAttribute('fetchpriority') === 'high' && img.getAttribute('loading') !== 'lazy' && !img.hasAttribute('data-tu-src') && img.closest('[data-tu-hero], .tu-clhero__media, header, .tu-hdr, .tu-cart, .tu-drawer')) return;
      img.__tuSkel = true;
      // 2026-10-06 (user: images were seen "drawn from top to bottom"): tu-skel hides the picture itself until it
      // is complete (CSS moves its pixels out of the box while the shimmer shows); was-waiting = fade in on arrival.
      img.classList.add('tu-skel');
      if (img.complete && img.naturalWidth > 0 && !img.hasAttribute('data-tu-src')) mark(img);
      else { img.classList.add('was-waiting'); img.__tuOnLoad = function () { onLoad(img); }; img.addEventListener('load', img.__tuOnLoad); img.addEventListener('error', img.__tuOnLoad); }
      if (io) io.observe(img);
    });
  }
  // 2026-10-06 (stuck clones): a cloned <img> (the Target Reviews rail clones its cards) copies the classes
  // (tu-skel was-waiting) but not the per-image load listener, so it never got is-loaded and stayed hidden. Load and error do
  // not bubble, but a capture listener on document sees every image's, whoever made it.
  function swept(e) { var t = e.target; if (t && t.tagName === 'IMG' && t.classList.contains('tu-skel') && !t.hasAttribute('data-tu-src') && !t.classList.contains('is-loaded')) mark(t); }
  document.addEventListener('load', swept, true);
  document.addEventListener('error', swept, true);
  // Belt and braces for an image that finished before it was ever listened to (cloned from a complete one).
  function sweep() { document.querySelectorAll('img.tu-skel:not(.is-loaded)').forEach(function (im) { if (im.complete && im.naturalWidth > 0 && !im.hasAttribute('data-tu-src')) mark(im); }); }
  window.addEventListener('load', function () { setTimeout(sweep, 500); setTimeout(sweep, 3000); });

  // 2026-10-06 CSS-background photos (October ad slides and cards, the "We supply the people who train" band): same
  // treatment as the <img>s. [data-tu-bgs] elements carry a .tu-bgs__veil (opaque base + sheen, see tu-system.css) over the
  // photo; the photo's URL is read from the computed background-image, probed with an Image, and when it has fully
  // arrived the element gets .is-bg-loaded and the veil fades out. Re-probes when a breakpoint swaps the URL.
  // A photo that was already cached gets .is-bg-fast (no fade). Errors also reveal, so a veil can never stay for ever.
  function arm(el, gate) {
    if (el.__tuArming) return; el.__tuArming = true;
    var go = function () { if (el.classList.contains('is-bg-armed')) return; el.classList.add('is-bg-armed'); bg(el); };
    gate.addEventListener('tu:band-load', go, { once: true });
    if ('IntersectionObserver' in window) { var o = new IntersectionObserver(function (es) { if (es.some(function (e) { return e.isIntersecting; })) { o.disconnect(); go(); } }, { rootMargin: '100% 0px' }); o.observe(el); }
    else window.addEventListener('load', go);
    setTimeout(function () { if (!document.querySelector('script[src*="tu-loader"]')) go(); }, 0);   // no loader on this page: nothing will fire band-load
  }
  function bg(el) {
    var lz = el.closest('[data-tu-bgs-lazy]'); if (lz && !lz.classList.contains('is-armed')) return;   // promo slides arm themselves (tu-promos.js)
    // 2026-10-06 (hero speed): any other [data-tu-bgs] element inside a [data-tu-band] section (the "We supply the people who
    // train" band) is not requested until it is within a screen of the viewport or tu-loader.js reaches its section in page
    // order. tu-system.css keeps background-image: none on it until it carries .is-bg-armed.
    var gate = !lz && el.closest('[data-tu-band]');
    if (gate && !el.classList.contains('is-bg-armed')) { arm(el, gate); return; }
    var m = /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(el).backgroundImage), url = m && m[1];
    if (!url) { el.classList.add('is-bg-loaded', 'is-bg-fast'); return; }
    if (el.__tuBgUrl === url) return;
    el.__tuBgUrl = url;
    el.classList.remove('is-bg-loaded', 'is-bg-fast');
    var im = new Image();
    var done = function () { if (el.__tuBgUrl === url) el.classList.add('is-bg-loaded'); };
    im.onload = im.onerror = done;
    im.src = url;
    if (im.complete && im.naturalWidth > 0) { el.classList.add('is-bg-fast'); done(); }
  }
  function bgScan(root) { (root || document).querySelectorAll('[data-tu-bgs]').forEach(bg); }
  var rz = 0; window.addEventListener('resize', function () { clearTimeout(rz); rz = setTimeout(function () { bgScan(); }, 200); });
  document.addEventListener('shopify:section:load', function (e) { bgScan(e.target); });
  window.TuSkel = { observe: function (root) { observe(root); bgScan(root); }, bg: bg };
  observe(document);
  bgScan(document);
  // Hero tiles: the photo shows only when complete (.is-lqip-done, set by each <img>'s onload in tu-hero-tiles.liquid).
  // Also on error, and for a photo that finished before this ran, so a tile can never stay on its preview for ever.
  document.querySelectorAll('.tu-hT__tile').forEach(function (t) {
    var im = t.querySelector('.tu-hT__img'); if (!im) return;
    // 2026-10-06: the small tiles start as a 1x1 GIF (data-tu-src); only the real photo counts.
    var done = function () { if (im.hasAttribute('data-tu-src') || im.naturalWidth <= 1) return; t.classList.add('is-lqip-done'); };
    if (im.complete && im.naturalWidth > 1 && !im.hasAttribute('data-tu-src')) done(); else { im.addEventListener('load', done); im.addEventListener('error', function () { t.classList.add('is-lqip-done'); }); }
  });
})();
